import { useEffect, useState } from "react";
import {
  MapContainer,
  Polyline,
  Marker,
  TileLayer,
} from "react-leaflet";
import {
  Box,
  Button,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-geometryutil"; // ✅ ใช้คำนวณ segment ที่ใกล้จุดคลิกที่สุด
import { listSegments } from "../../api/route";

// ✅ icon จุดปกติ
const dotIcon = L.divIcon({
  className: "custom-dot",
  html: `<div style="
    width:12px;
    height:12px;
    background:blue;
    border:2px solid white;
    border-radius:50%;
    cursor:pointer;
  "></div>`,
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// ✅ icon จุดที่ถูกเลือก (highlight)
const dotIconSelected = L.divIcon({
  className: "custom-dot-selected",
  html: `<div style="
    width:16px;
    height:16px;
    background:red;
    border:2px solid yellow;
    border-radius:50%;
    cursor:pointer;
  "></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

export default function RouteEditor() {
  const [segments, setSegments] = useState<any[]>([]);
  const [mode, setMode] = useState<"delete" | "add" | null>(null);
  const [selected, setSelected] = useState<{
    segIndex: number;
    pointIndex: number;
  } | null>(null);

  // โหลด route จาก API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await listSegments();
        setSegments(data);
      } catch (err) {
        console.error("โหลด segments ไม่สำเร็จ:", err);
      }
    };
    fetchData();
  }, []);

  // อัพเดทตำแหน่งหลังลาก marker
  const updatePoint = (
    segIndex: number,
    pointIndex: number,
    newPos: [number, number]
  ) => {
    setSegments((prev) =>
      prev.map((seg, i) =>
        i === segIndex
          ? {
              ...seg,
              polyline: seg.polyline.map((p: any, j: number) =>
                j === pointIndex ? { lat: newPos[0], lng: newPos[1] } : p
              ),
            }
          : seg
      )
    );
  };

  // ลบจุด
  const deletePoint = (segIndex: number, pointIndex: number) => {
    setSegments((prev) =>
      prev.map((seg, i) =>
        i === segIndex
          ? {
              ...seg,
              polyline: seg.polyline.filter(
                (_: any, j: number) => j !== pointIndex
              ),
            }
          : seg
      )
    );
    setSelected(null);
  };

  // ✅ เพิ่มจุดตรงกลางเส้น (เฉพาะเมื่อคลิก Polyline)
  const addPointOnLine = (
    segIndex: number,
    clickLatLng: [number, number],
    map: L.Map
  ) => {
    setSegments((prev) =>
      prev.map((seg, i) => {
        if (i !== segIndex) return seg;
        const poly = seg.polyline;
        if (poly.length < 2) return seg;

        let minDist = Infinity;
        let insertIndex = 0;

        // หา segment ที่ใกล้จุดคลิกที่สุด
        for (let j = 0; j < poly.length - 1; j++) {
          const a = L.latLng(poly[j].lat, poly[j].lng);
          const b = L.latLng(poly[j + 1].lat, poly[j + 1].lng);
          const p = L.latLng(clickLatLng[0], clickLatLng[1]);

          const dist = (L as any).GeometryUtil.distanceSegment(map, p, a, b);
          if (dist < minDist) {
            minDist = dist;
            insertIndex = j + 1;
          }
        }

        // แทรกจุดใหม่เข้ากลาง polyline
        const newPolyline = [
          ...poly.slice(0, insertIndex),
          { lat: clickLatLng[0], lng: clickLatLng[1] },
          ...poly.slice(insertIndex),
        ];

        return { ...seg, polyline: newPolyline };
      })
    );
  };

  const handleSave = async () => {
    await fetch("/api/routes", {
      method: "POST",
      body: JSON.stringify(segments),
      headers: { "Content-Type": "application/json" },
    });
    alert("บันทึกเส้นทางสำเร็จ");
  };

  return (
    <Stack direction="row" spacing={2} sx={{ height: "100vh" }}>
      {/* Sidebar */}
      <Box sx={{ width: 300, p: 2 }}>
        <Typography variant="h6">เครื่องมือ</Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, value) => setMode(value)}
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="delete">🗑 ลบ</ToggleButton>
          <ToggleButton value="add">➕ เพิ่ม</ToggleButton>
        </ToggleButtonGroup>

        <Button variant="contained" fullWidth onClick={handleSave}>
          บันทึก
        </Button>
      </Box>

      {/* Map */}
      <Box sx={{ flex: 1 }}>
        <MapContainer
          center={[18.7487, 98.9253]}
          zoom={15}
          style={{ height: "100vh", width: "100%" }}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {segments.map((seg, segIndex) => {
            const positions = seg.polyline.map(
              (p: any) => [p.lat, p.lng] as [number, number]
            );
            return (
              <div key={segIndex}>
                <Polyline
                  positions={positions}
                  color="blue"
                  eventHandlers={{
                    click: (e) => {
                      if (mode === "add") {
                        const map = e.target._map as L.Map;
                        const clickLatLng: [number, number] = [
                          e.latlng.lat,
                          e.latlng.lng,
                        ];
                        addPointOnLine(segIndex, clickLatLng, map);
                      }
                    },
                  }}
                />
                {positions.map((pos : any, pointIndex : any) => (
                  <Marker
                    key={pointIndex}
                    position={pos}
                    icon={
                      selected?.segIndex === segIndex &&
                      selected?.pointIndex === pointIndex
                        ? dotIconSelected
                        : dotIcon
                    }
                    draggable={true} // ✅ เลื่อนจุดได้ตลอดเวลา
                    eventHandlers={{
                      dragend: (e) => {
                        const newLatLng = (e.target as L.Marker).getLatLng();
                        updatePoint(segIndex, pointIndex, [
                          newLatLng.lat,
                          newLatLng.lng,
                        ]);
                      },
                      click: () => {
                        if (mode === "delete") {
                          deletePoint(segIndex, pointIndex);
                        } else {
                          setSelected({ segIndex, pointIndex });
                        }
                      },
                    }}
                  />
                ))}
              </div>
            );
          })}
        </MapContainer>
      </Box>
    </Stack>
  );
}
